import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { deckStorageService } from '@/services/deck-storage-service';
import { Capacitor } from '@capacitor/core';

export function useDeckStorage() {
  const { token } = useAuth();
  const [isInitializing, setIsInitializing] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  
  // Fetch the deck manifest from the API
  const { data: decks, isLoading, error } = useQuery({
    queryKey: ['deck-manifest'],
    queryFn: async () => {
      if (!isNative) return null;
      
      let apiUrl = '/api/deck/manifest';
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch deck manifest');
      }
      
      return response.json();
    },
    enabled: isNative && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Update the deck manifest in the storage service when data is available
  useEffect(() => {
    if (!isNative || !decks || isLoading) return;
    
    const updateManifest = async () => {
      try {
        setIsInitializing(true);
        await deckStorageService.updateDeckManifest(decks);
        setIsInitializing(false);
      } catch (error) {
        console.error('Error updating deck manifest:', error);
        setIsInitializing(false);
      }
    };
    
    updateManifest();
  }, [decks, isLoading, isNative]);
  
  // Return values and functions for the hook consumer
  return {
    isInitializing,
    isLoading,
    hasLocalStorage: isNative,
    error,
    
    // Function to download a specific deck
    downloadDeck: (deckId: number, onProgress?: (progress: number) => void) => {
      if (!isNative) return Promise.resolve();
      return deckStorageService.queueDeckDownload(deckId, onProgress);
    },
    
    // Function to download all decks
    downloadAllDecks: () => {
      if (!isNative) return Promise.resolve();
      return deckStorageService.downloadAllDecks();
    },
    
    // Function to delete a downloaded deck
    deleteDeck: (deckId: number) => {
      if (!isNative) return Promise.resolve();
      return deckStorageService.deleteDeck(deckId);
    }
  };
}